import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Quote, Play } from 'lucide-react';

const highlights = [
  'Familiengeführter Meisterbetrieb seit 1966 – fast 60 Jahre Erfahrung',
  'Unser Team aus erfahrenen Elektrikern arbeitet nach DIN-VDE-Normen',
  'Auch im Raum Köln vertreten – durch Wohnungsbau und Immobilienservice',
  'Termingerecht, sauber und kundenorientiert',
  'Mitglied der Innung Köln & Kreishandwerkerschaft Bonn/Rhein-Sieg',
];

export default function UeberUnsSection() {
  return (
    <section id="ueber-uns" data-testid="ueber-uns-section" className="py-24 sm:py-32 bg-[#F4F8F4] relative overflow-hidden">
      {/* Decorative element */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#2D5A27]/5 rounded-full -translate-y-1/2 translate-x-1/3" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#10B981] font-['Manrope'] text-sm font-semibold tracking-[0.2em] uppercase">
              Über uns
            </span>
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-[#1A3A22] tracking-tight mt-3 mb-6">
              Ihr Elektriker in Bonn – <span className="text-[#2D5A27]">seit 1966</span>
            </h2>
            <p className="font-['Manrope'] text-gray-600 text-base sm:text-lg leading-relaxed mb-6">
              Gegründet 1966 als Gebrüder Daniels GbR und 1984 umfirmiert in die
              K.H. und B. Daniels Elektroinstallationen GmbH, steht unser Unternehmen seit fast
              60 Jahren für erstklassige Elektroarbeit in Bonn und dem Rhein-Sieg-Kreis.
              Dabei verbinden wir handwerkliche Präzision mit moderner Technik, persönlichem
              Service und langjähriger Erfahrung.
            </p>
            <p className="font-['Manrope'] text-gray-600 text-base sm:text-lg leading-relaxed mb-8">
              Unser Antrieb: Jedes Projekt so umzusetzen, als wäre es unser eigenes Zuhause.
              Unser Team aus erfahrenen Elektrikern steht für Qualität, Sauberkeit und Zuverlässigkeit.
            </p>

            <div className="space-y-3 mb-10">
              {highlights.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0" strokeWidth={2} />
                  <span className="font-['Manrope'] text-[#1A3A22] font-medium text-sm sm:text-base">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Video + Quote */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* Video */}
            <div className="relative group cursor-pointer" data-testid="video-wrapper" onClick={(e) => {
              const video = e.currentTarget.querySelector('video');
              if (video.paused) { video.play(); e.currentTarget.querySelector('[data-testid="video-play-overlay"]').style.display = 'none'; }
            }}>
              <video
                src="/unternehmensvideo.mp4"
                controls
                playsInline
                preload="metadata"
                className="w-full h-[350px] sm:h-[420px] object-cover bg-[#1A3A22]"
                data-testid="ueber-uns-video"
                onPlay={(e) => { e.target.closest('[data-testid="video-wrapper"]').querySelector('[data-testid="video-play-overlay"]').style.display = 'none'; }}
                onPause={(e) => { e.target.closest('[data-testid="video-wrapper"]').querySelector('[data-testid="video-play-overlay"]').style.display = 'flex'; }}
              >
                Ihr Browser unterstützt das Video-Format nicht.
              </video>
              <div
                data-testid="video-play-overlay"
                className="absolute inset-0 flex items-center justify-center bg-[#1A3A22]/30 transition-opacity duration-300"
              >
                <div className="w-20 h-20 bg-[#2D5A27] rounded-full flex items-center justify-center shadow-2xl group-hover:bg-[#10B981] transition-all duration-300 group-hover:scale-110">
                  <Play className="w-9 h-9 text-white ml-1" fill="white" strokeWidth={0} />
                </div>
              </div>
            </div>
            {/* CEO Quote below */}
            <div className="bg-[#1A3A22] p-6 sm:p-8" data-testid="ceo-quote">
              <div className="flex items-start gap-4">
                <Quote className="w-8 h-8 text-[#10B981] flex-shrink-0 mt-1" strokeWidth={1.5} />
                <div>
                  <p className="font-['Outfit'] text-white text-base sm:text-lg italic leading-relaxed mb-4">
                    &ldquo;Qualität bedeutet für uns, dass unsere Kunden sich keine Sorgen machen müssen –
                    nicht heute und nicht in 20 Jahren.&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#2D5A27] rounded-full flex items-center justify-center">
                      <span className="text-white font-['Outfit'] font-bold text-sm">D</span>
                    </div>
                    <div>
                      <span className="text-white font-['Outfit'] font-semibold text-sm block">
                        Geschäftsführung
                      </span>
                      <span className="text-white/60 font-['Manrope'] text-xs">
                        Daniels Elektroinstallationen
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
